import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { IntervalEarTrainer } from "@/components/et/IntervalEarTrainer";
import { IntervalSingBack } from "@/components/et/IntervalSingBack";
import { NextUp } from "@/components/Callouts";

export default function EarTraining4() {
  const { lesson, course, accent } = findLesson("ear-training", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET4-Intervals-by-Ear-Animated.mp4"
        caption="Intervals by ear — category first (mood), then the specific name"
        hint="≈ 32 sec · watch first"
      />
      <Chapter number="04" eyebrow="Ear Training · Chapter Four" title="Hearing intervals — the twelve fingerprints of music">
        <Prose>
          <p>
            Welcome to the centrepiece of ear training. Every melody, every chord, every
            song is built from <strong>intervals</strong> — the distance between two notes.
            There are exactly twelve unique intervals (after which they repeat at the
            octave), and each one has a distinct <em>emotional fingerprint</em> — a vibe
            you can train your ear to recognise instantly.
          </p>
          <p>
            Once you've internalised these twelve, you can transcribe any melody by ear:
            you hear "interval up... interval down..." and the notes fall into place.
          </p>
        </Prose>

        <MTQuote>
          Interval is the vibe when two notes collide with each other. And first of all, you
          need to understand that vibe sonically. The vibe feels different for each
          interval.
        </MTQuote>

        <NoteImage
          src="/notes/ear-training/finals/04a-vibes-resolution-anticipation-mystery-tension.png"
          caption="Jason's note: intervals classified by VIBE — Resolution (P5, M3, m3, octave, unison — feel 'finished'), Anticipation (M2, P4 — pull you forward), Mystery (M6, m6 — open, dreamy), Tension (m2/♭2, M7, tritone — beg to resolve)."
        />
      </Chapter>

      <Chapter eyebrow="Melodic vs harmonic" title="Two ways an interval can sound">
        <Glossary>
          <Term name="Melodic interval" def="The two notes are played one after the other (a melody). Easier for beginners — you hear the 'distance' as a leap." />
          <Term name="Harmonic interval" def="The two notes are played together (a chord). Harder — you hear them as a single 'colour' rather than two notes." />
        </Glossary>

        <Prose>
          <p>
            Always learn melodic first, harmonic second. The drill below lets you switch
            between the two — start in melodic mode until you can identify all twelve
            confidently, then move to harmonic.
          </p>
        </Prose>

        <IntervalEarTrainer />
        <IntervalSingBack />
      </Chapter>

      <Chapter eyebrow="The four-vibe trick" title="Don't memorise twelve names — memorise four moods">
        <Prose>
          <p>
            Here's Jason's secret. Don't try to memorise twelve interval names cold. Memorise
            <strong> four moods</strong>, then learn which intervals fall under each mood.
            The vibe is what you can actually hear; the name follows.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/04b-12-intervals-with-C-F-Eb-Eb-columns.png"
          caption="The 12 intervals tabulated against C, F, E♭ (M3 column) and E♭ (m3 column) — practical reference for testing your guesses. Memorise the columns and you've memorised every interval in every key."
        />

        <NoteImage
          src="/notes/ear-training/finals/04g-swaras-and-intervals-Indian.png"
          caption="The Indian swara mapping: r₁/r₂/r₃ = m2/M2/aug2; g₁/g₂/g₃ = dim3/m3/M3; m₁/m₂ = P4/aug4; d₁/d₂/d₃ = m6/M6/aug6; n₁/n₂/n₃ = dim7/m7/M7. Same intervals, two vocabularies."
        />
      </Chapter>

      <Chapter eyebrow="Famous-song mnemonics" title="Anchor each interval to a song you already know">
        <Glossary>
          <Term name="Minor 2nd ↑" def='"Jaws" theme — the two-note shark warning.' />
          <Term name="Major 2nd ↑" def='"Happy Birthday" — first two notes ("Hap-py").' />
          <Term name="Minor 3rd ↑" def='"Greensleeves" — first two notes; or the "London Bridge" first interval.' />
          <Term name="Major 3rd ↑" def='"When the Saints" — first two notes; or "Oh When the Saints".' />
          <Term name="Perfect 4th ↑" def='"Here Comes the Bride" — first two notes; or "Amazing Grace".' />
          <Term name="Tritone ↑" def='"The Simpsons" theme — Maaargie!; or "Maria" from West Side Story.' />
          <Term name="Perfect 5th ↑" def='"Twinkle Twinkle Little Star" — first two notes; or "Star Wars" main theme.' />
          <Term name="Minor 6th ↑" def='"The Entertainer" (Scott Joplin) — the iconic opening leap.' />
          <Term name="Major 6th ↑" def='"NBC chimes" — G-E-C; or "My Bonnie Lies Over the Ocean".' />
          <Term name="Minor 7th ↑" def='"Somewhere" from West Side Story — "There&apos;s a place for us".' />
          <Term name="Major 7th ↑" def='"Take On Me" by a-ha — the chorus leap.' />
          <Term name="Octave ↑" def='"Somewhere Over the Rainbow" — "Some-where".' />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The interval families" title="Sixths, sevenths, seconds, tritone, extensions">
        <Prose>
          <p>
            Beyond the basic twelve, intervals have inversions and family relationships
            worth knowing. A <strong>major 6th</strong> inverted becomes a minor 3rd. A
            <strong> minor 7th</strong> inverted becomes a major 2nd. Two rules run in
            parallel, and it's worth keeping them apart. The <em>numbers</em> always add up
            to <strong>nine</strong> — 6 + 3, 7 + 2, 5 + 4 — which is the old "rule of nine".
            The <em>semitones</em> always add up to <strong>twelve</strong>, because that is
            the octave you are folding the note through: a major 6th is 9 semitones, its
            inversion 3. Quality flips too — major becomes minor, diminished becomes
            augmented, and perfect stays perfect. That's why fluency in one inversion gives
            you the other free.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/04c-sixth-intervals.png"
          caption="The sixth-interval family. Maj6 = P5 + 2. Min6 = P5 + 1. Dim7 (often called 'sixth') = octave − 3 = m7 − 2. Aug6 = P5 + 1 (enharmonic with m7). C major 6 = C E G A. Cdim7 = C E♭ G♭ A."
        />

        <NoteImage
          src="/notes/ear-training/finals/04d-seventh-intervals.png"
          caption="The seventh family. Major 7th = octave − 1 = M7. Minor 7th = octave − 2 = m7. C7 = C major triad + ♭7 = C E G B♭. The sevenths add the most colour to a chord with one note."
        />

        <NoteImage
          src="/notes/ear-training/finals/04e-tritone-aug4-dim5-whole-tone.png"
          caption="The tritone (♯4 / ♭5) — three whole tones from the root, splitting the octave perfectly in half. Lydian uses ♯4 (CDEF♯GAB) for its dreamy lift; Locrian uses ♭5 (CD♭E♭FG♭A♭B♭) for its ominous instability. The whole-tone scale (CDEF♯G♯B♭C) marches entirely in major 2nds."
        />

        <NoteImage
          src="/notes/ear-training/finals/04f-second-intervals-and-extensions.png"
          caption="The second-interval family + jazz extensions. M2 = root + 2 semitones; m2 = root + 1. The 9 (M2 above the octave), 11 (P4 above the octave), 13 (M6 above the octave) are 'extensions' — added on top of a 7th chord to create jazz colour."
        />
      </Chapter>

      <NextUp
        to="/lesson/ear-training-5"
        label="Chapter 5 · Melodic Curves 1"
        why="Once you can hear intervals, you can hear melodies as a series of high-and-low movements. Jason's 'melodic curves' visualisation makes transcription ten times faster."
      />
    </LessonShell>
  );
}
